import React from 'react';
import { Box, Text } from '@cowbell/ui';

import { rem } from '../../../components/panels/panelLayout';
import { useDashboardPanelTokens } from './dashboardPanelTokens';

/**
 * Placeholder severities, per the design. The breakdown never arrived, so these
 * name the shape of what is missing rather than any real count.
 */
const PLACEHOLDER_SEVERITIES = ['critical', 'high', 'medium'];

// Two rows, not three: enough to read as a list that is missing, without
// padding the tile out with more placeholder than message.
const PLACEHOLDER_ROWS = [1, 0.7];

/**
 * The report exists but its findings summary did not come back.
 *
 * Deliberately not an error banner: the document itself is complete, and the
 * reader's next step -- downloading it -- is unaffected. The placeholders keep
 * the tile's shape so the summary's absence reads as missing detail rather than
 * as a broken tile, and the copy says plainly which half failed.
 */
export function RiskFindingsUnavailable() {
  const tokens = useDashboardPanelTokens();

  return (
    <Box data-qa="RISK_FINDINGS_UNAVAILABLE" style={{ marginTop: rem(10) }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: rem(10) }}>
        <Box
          aria-hidden="true"
          style={{
            width: rem(28),
            height: rem(4),
            borderRadius: rem(999),
            backgroundColor: tokens.inkFaint,
          }}
        />
        <Text style={{ fontSize: rem(13), color: tokens.inkFaint }}>
          findings unavailable
        </Text>
      </Box>

      <Box
        aria-hidden="true"
        style={{
          marginTop: rem(10),
          height: rem(6),
          borderRadius: rem(999),
          backgroundColor: tokens.line,
        }}
      />

      <Box
        aria-hidden="true"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: rem(14),
          marginTop: rem(8),
        }}
      >
        {PLACEHOLDER_SEVERITIES.map((label) => (
          <Box
            key={label}
            style={{ display: 'flex', alignItems: 'center', gap: rem(5) }}
          >
            <Box
              style={{
                width: rem(7),
                height: rem(7),
                flexShrink: 0,
                borderRadius: '50%',
                backgroundColor: tokens.line,
              }}
            />
            <Text
              component="span"
              style={{ fontSize: rem(12), color: tokens.inkFaint }}
            >
              &mdash;
            </Text>
            <Text
              component="span"
              style={{ fontSize: rem(12), color: tokens.inkFaint }}
            >
              {label}
            </Text>
          </Box>
        ))}
      </Box>

      <Box
        aria-hidden="true"
        style={{
          marginTop: rem(12),
          paddingTop: rem(12),
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        {PLACEHOLDER_ROWS.map((width, index) => (
          <Box
            key={`placeholder-${index}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(0, 1fr)',
              columnGap: rem(9),
              alignItems: 'center',
              padding: `${rem(5)} 0`,
            }}
          >
            <Box
              style={{
                width: rem(7),
                height: rem(7),
                borderRadius: '50%',
                backgroundColor: tokens.line,
              }}
            />
            <Box
              style={{
                height: rem(9),
                width: `${width * 100}%`,
                borderRadius: rem(999),
                backgroundColor: tokens.line,
              }}
            />
          </Box>
        ))}
      </Box>

      <Text
        style={{
          marginTop: rem(12),
          fontSize: rem(12.5),
          lineHeight: 1.5,
          color: tokens.inkBody,
        }}
      >
        The severity breakdown didn&apos;t come back with this report. The
        document itself is complete and safe to download.
      </Text>
    </Box>
  );
}

export default RiskFindingsUnavailable;
